// =============================================================================
// screens/LeaderApplyScreen.tsx — apply to become a CRYB Leader.
//
// A normal user pitches to lead a community; an admin approves. While an
// application is pending we show its status instead of the form; once approved
// the drawer swaps this entry for the Leader Dashboard.
//   GET  /leaders/me      — current status (isLeader / pending / rejected / none)
//   POST /leaders/apply   — submit { reason, focus }
// =============================================================================
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { View, Text, TextInput, StyleSheet, ScrollView, ActivityIndicator, Alert, TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '../components/ui/Icon';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import { GlassCard, GlassButton, GlassSurface } from '../components/glass';
import { colors, spacing, radius, fonts, typographyScale } from '../constants/theme';
import { api } from '../services/api';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'LeaderApply'>;

const REASON_MAX = 1000;
const FOCUS_MAX = 200;

export default function LeaderApplyScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<'none' | 'pending' | 'approved' | 'rejected'>('none');
  const [reason, setReason] = useState('');
  const [focus, setFocus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useLayoutEffect(() => { navigation.setOptions({ headerShown: false }); }, [navigation]);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api<any>('/leaders/me');
    if (res.success && res.data) {
      setStatus(res.data.isLeader ? 'approved' : (res.data.status ?? 'none'));
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    const res = await api<any>('/leaders/apply', {
      method: 'POST',
      body: JSON.stringify({ reason: reason.trim() || undefined, focus: focus.trim() || undefined }),
    });
    setSubmitting(false);
    if (res.success) {
      setStatus('pending');
      Alert.alert('Application sent', 'Your request to become a CRYB Leader is under review. You’ll be notified once it’s approved.');
    } else {
      Alert.alert('Error', res.error || 'Could not submit your application. Please try again.');
    }
  }, [reason, focus, submitting]);

  const Header = (
    <GlassSurface variant="bar" radius={0} sheen={false} surfaceColor="transparent"
      style={[styles.header, { paddingTop: insets.top }]} contentStyle={styles.headerContent}>
      <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }} accessibilityRole="button" accessibilityLabel="Back">
        <Ionicons name="chevron-back" size={26} color={colors.ink} />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>CRYB Leader</Text>
      <View style={{ width: 26 }} />
    </GlassSurface>
  );

  if (loading) {
    return (
      <View style={styles.screen}><ParticleBackground theme="light" />{Header}
        <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      {Header}
      <ScrollView contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + spacing.xxxl }]} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
        <Asset3D name="trophy" size={72} style={{ marginTop: spacing.md, marginBottom: spacing.md }} />
        <Text style={styles.title}>Lead a community on CRYB</Text>
        <Text style={styles.subtitle}>
          CRYB Leaders create and run communities with creator tools: customize the space, manage channels, moderate, and grow your people.
        </Text>

        {status === 'pending' ? (
          <GlassCard style={styles.statusCard} padding={spacing.lg}>
            <Ionicons name="hourglass-outline" size={28} color={colors.primary} />
            <Text style={styles.statusTitle}>Application under review</Text>
            <Text style={styles.statusSub}>We’re reviewing your request. You’ll get a notification once you’re approved.</Text>
          </GlassCard>
        ) : status === 'rejected' ? (
          <GlassCard style={styles.statusCard} padding={spacing.lg}>
            <Ionicons name="refresh-outline" size={28} color={colors.primary} />
            <Text style={styles.statusTitle}>Not approved last time</Text>
            <Text style={styles.statusSub}>You can refine your pitch and apply again below.</Text>
          </GlassCard>
        ) : null}

        {status !== 'pending' && (
          <GlassCard style={styles.formCard} padding={spacing.lg}>
            <Text style={styles.label}>Why do you want to lead?</Text>
            <TextInput
              style={styles.input} value={reason} onChangeText={(v) => v.length <= REASON_MAX && setReason(v)}
              placeholder="Tell us your vision for the community…" placeholderTextColor={colors.faint}
              multiline maxLength={REASON_MAX} textAlignVertical="top"
            />
            <Text style={styles.label}>Community focus (optional)</Text>
            <TextInput
              style={styles.inputSingle} value={focus} onChangeText={(v) => v.length <= FOCUS_MAX && setFocus(v)}
              placeholder="e.g. DeFi, NFTs, Bitcoin, your niche" placeholderTextColor={colors.faint} maxLength={FOCUS_MAX}
            />
            <GlassButton title={submitting ? 'Sending…' : 'Apply to become a Leader'} onPress={submit} variant="primary" disabled={submitting} loading={submitting} style={{ marginTop: spacing.md }} />
          </GlassCard>
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  header: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.cardBorder },
  headerContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.md, paddingBottom: spacing.sm },
  headerTitle: { ...typographyScale.h3, color: colors.ink, ...fonts.semibold },
  content: { paddingHorizontal: spacing.xl, paddingTop: 100, alignItems: 'center' },
  title: { ...typographyScale.h1, color: colors.ink, ...fonts.bold, textAlign: 'center', marginBottom: spacing.sm },
  subtitle: { ...typographyScale.body, color: colors.muted, textAlign: 'center', lineHeight: 22, marginBottom: spacing.lg },
  statusCard: { width: '100%', alignItems: 'center', marginBottom: spacing.lg },
  statusTitle: { ...typographyScale.h3, color: colors.ink, ...fonts.bold, marginTop: spacing.sm },
  statusSub: { ...typographyScale.body, color: colors.muted, textAlign: 'center', marginTop: spacing.xs, lineHeight: 20 },
  formCard: { width: '100%' },
  label: { ...typographyScale.caption, color: colors.muted, ...fonts.semibold, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: spacing.xs, marginTop: spacing.sm },
  input: { ...fonts.regular, fontSize: 16, color: colors.ink, backgroundColor: 'rgba(255,255,255,0.55)', borderRadius: radius.md, padding: spacing.md, borderWidth: 1, borderColor: colors.cardBorder, height: 110 },
  inputSingle: { ...fonts.regular, fontSize: 16, color: colors.ink, backgroundColor: 'rgba(255,255,255,0.55)', borderRadius: radius.md, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderWidth: 1, borderColor: colors.cardBorder },
});
